import { Badge, Button, Card, Group, Stack, Table, Text } from "@mantine/core";
import { IconArrowRight, IconFlask } from "@/lib/icons";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { formatDate, formatDateTime } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { listLabOrders } from "@/lib/api/laboratory";

export function LabWorklistPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["lab-orders", "pending"],
    queryFn: () => listLabOrders({ status: "pending", limit: 100 }),
    refetchInterval: 30_000,
  });

  return (
    <Stack gap="lg">
      <PageHeader title={t("laboratory.title")} />

      <Card padding={0}>
        <Table.ScrollContainer minWidth={760}>
          <Table striped highlightOnHover verticalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("laboratory.fields.created_at")}</Table.Th>
                <Table.Th>{t("laboratory.fields.tests_count")}</Table.Th>
                <Table.Th>{t("laboratory.fields.status")}</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text c="dimmed" ta="center" py="md">
                      {t("common.loading")}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {data?.items.length === 0 && !isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <EmptyState
                      icon={<IconFlask size={32} strokeWidth={1.5} />}
                      title={t("laboratory.empty")}
                    />
                  </Table.Td>
                </Table.Tr>
              )}
              {data?.items.map((o) => (
                <Table.Tr key={o.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {formatDateTime(o.created_at)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      {o.tests.slice(0, 5).map((tst) => (
                        <Badge
                          key={tst}
                          variant="light"
                          color="blue"
                          size="sm"
                          radius="sm"
                        >
                          {tst}
                        </Badge>
                      ))}
                      {o.tests.length > 5 && (
                        <Text c="dimmed" size="sm" fw={500}>
                          +{o.tests.length - 5}
                        </Text>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <StatusBadge status={o.status} i18nPrefix="laboratory.statuses" />
                  </Table.Td>
                  <Table.Td>
                    <Button
                      size="xs"
                      rightSection={<IconArrowRight size={14} strokeWidth={1.8} />}
                      onClick={() => navigate(`/lab-orders/${o.id}`)}
                    >
                      {t("laboratory.enter_results")}
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>
    </Stack>
  );
}
